import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { ApiError, attachmentUrl, deleteAttachment, uploadAttachment } from '../../api/client'

/**
 * M9.4：文件列（file）。
 *
 * 值格式：[{ id, name, size, mime }]
 *
 * **两阶段提交**（这一版的核心修正）：
 * 上传/删除必须立刻落到服务端（上传要拿 id，删除要清磁盘），但**单元格的值只在点「完成」时提交**。
 * 于是本次编辑里"上传了但还没提交"的附件属于**暂存**状态：
 *   · 取消 / 点遮罩关掉 → 把这些暂存附件全部删掉（否则就是找不到也删不掉的幽灵文件）
 *   · 点 × 删掉的 → 服务端删了就同步从列表移除；即使服务端回 not found 也照样移除（本地为准）
 */

export interface AttachmentInfo {
  id: number
  name: string
  size: number
  mime: string
}

/** 解析单元格值；单值/非法一律当空 */
export function parseAttachments(raw: string): AttachmentInfo[] {
  if (raw.trim() === '' || !raw.trim().startsWith('[')) return []
  try {
    const arr: unknown = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    const out: AttachmentInfo[] = []
    for (const item of arr) {
      if (typeof item !== 'object' || item === null) continue
      const o = item as { id?: unknown; name?: unknown; size?: unknown; mime?: unknown }
      const id = typeof o.id === 'number' ? o.id : Number(o.id)
      if (!Number.isFinite(id)) continue
      out.push({
        id,
        name: typeof o.name === 'string' ? o.name : '未命名文件',
        size: typeof o.size === 'number' ? o.size : 0,
        mime: typeof o.mime === 'string' ? o.mime : '',
      })
    }
    return out
  } catch {
    return []
  }
}

export function formatSize(bytes: number): string {
  if (bytes <= 0) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** 与服务端默认值一致的单文件上限（配置项 upload.max_bytes） */
const MAX_UPLOAD_BYTES = 32 * 1024 * 1024

/** 从当前地址栏取看板 id */
function currentBoardId(): number {
  const m = /\/boards\/(\d+)/.exec(window.location.pathname)
  return m === null ? 0 : Number(m[1])
}

export interface FileEditorProps {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
  itemId: number
  columnKey: string
}

export function FileEditor({ initial, busy, onCommit, onCancel, itemId, columnKey }: FileEditorProps) {
  const [files, setFiles] = useState<AttachmentInfo[]>(() => parseAttachments(initial))
  const [pct, setPct] = useState<number | null>(null)
  const [error, setError] = useState('')
  // 每次选完文件就把 input 整个换掉：原生 file 输入复用时会残留上一批 File（实测 B 上传成了 A）
  const [inputKey, setInputKey] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ x: number; y: number; w: number } | null>(null)

  // 本次编辑里"上传了但还没提交"的附件：取消/卸载时要清理，否则会变成幽灵文件
  const pendingIds = useRef<number[]>([])
  // 是否已提交（提交后卸载不再清理）
  const committed = useRef(false)
  // 卸载回调里读得到最新值
  const pendingRef = useRef<number[]>([])
  pendingRef.current = pendingIds.current

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect()
    if (r !== undefined) {
      const w = 340
      const maxX = Math.max(8, window.innerWidth - w - 8)
      setPos({ x: Math.max(8, Math.min(Math.round(r.left), maxX)), y: Math.round(r.bottom + 8), w })
    }
  }, [])

  // 卸载兜底：没提交就把暂存附件删掉
  useEffect(() => {
    return () => {
      if (committed.current) return
      for (const id of pendingRef.current) {
        void deleteAttachment(id).catch(() => {
          /* 尽力而为 */
        })
      }
    }
  }, [])

  const boxStyle = { left: pos?.x ?? 0, top: pos?.y ?? 0, minWidth: pos?.w ?? 340 }

  /**
   * 上传若干文件。
   * 1) 选中后立刻把 FileList 快照成数组并清空 input.value ——
   *    原生 file 输入复用时，第二次 change 可能把上一次的 File 带进来。
   * 2) 成功后记进 pendingIds，取消时才能清理。
   */
  const upload = async (picked: File[]) => {
    if (picked.length === 0) return
    const boardId = currentBoardId()
    setError('')
    for (const f of picked) {
      if (f.size > MAX_UPLOAD_BYTES) {
        setError(`「${f.name}」${formatSize(f.size)} 超过上限 ${formatSize(MAX_UPLOAD_BYTES)}，未上传`)
        continue
      }
      try {
        setPct(0)
        const info = await uploadAttachment(boardId, itemId, columnKey, f, (p) => setPct(p))
        pendingIds.current.push(info.id)
        setFiles((prev) => {
          const next = [...prev, info]
          return next
        })
      } catch (e) {
        setError(
          e instanceof ApiError && e.status === 0
            ? '上传被服务器拒绝（可能超过大小上限，连接已断开）'
            : e instanceof Error
              ? e.message
              : '上传失败',
        )
      } finally {
        setPct(null)
      }
    }
  }

  /** 删除：服务端删（失败也继续），本地列表一定移除 */
  const remove = async (id: number) => {
    setError('')
    try {
      await deleteAttachment(id)
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) {
        setError(e instanceof Error ? e.message : '删除失败')
      }
    } finally {
      pendingIds.current = pendingIds.current.filter((x) => x !== id)
      setFiles((prev) => prev.filter((f) => f.id !== id))
    }
  }

  /** 取消：删掉本次上传但未提交的附件，再关闭 */
  const cancel = () => {
    for (const id of pendingIds.current) {
      void deleteAttachment(id).catch(() => {
        /* 尽力而为 */
      })
    }
    pendingIds.current = []
    onCancel()
  }

  /** 完成：提交当前列表 */
  const commit = () => {
    committed.current = true
    pendingIds.current = []
    onCommit(files.length === 0 ? '' : JSON.stringify(files))
  }

  return (
    <span ref={anchor} className="popup-anchor">
      <div className="panel-backdrop" onClick={cancel} />
      <div
        className="status-panel popup-panel file-panel"
        style={boxStyle}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="popup-body">
          {files.length === 0 ? (
            <div className="file-empty">还没有附件</div>
          ) : (
            <ul className="file-list">
              {files.map((f) => (
                <li key={f.id} className="file-row">
                  <a className="file-name" href={attachmentUrl(f.id)} title={`${f.name}（${formatSize(f.size)}）`}>
                    {f.name}
                  </a>
                  <span className="file-size">{formatSize(f.size)}</span>
                  <button
                    type="button"
                    className="file-del"
                    aria-label={`删除 ${f.name}`}
                    disabled={busy}
                    onClick={() => void remove(f.id)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {pct !== null && (
            <div className="file-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="file-progress__bar" style={{ width: `${pct}%` }} />
              <span className="file-progress__text">已上传 {pct}%</span>
            </div>
          )}

          {error !== '' && <div className="file-error">{error}</div>}

          <input
            key={inputKey}
            ref={inputRef}
            type="file"
            multiple
            className="file-input"
            onChange={(e) => {
              const picked = e.target.files === null ? [] : Array.from(e.target.files)
              // 先快照、再清空、最后把整个 input 换新（三保险，避免残留 File 被重复提交）
              e.target.value = ''
              setInputKey((k) => k + 1)
              void upload(picked)
            }}
          />
          <button
            type="button"
            className="file-upload-btn"
            disabled={busy || pct !== null}
            onClick={() => inputRef.current?.click()}
          >
            <span className="file-upload-btn__icon">＋</span>
            选择文件
          </button>

          <div className="popup-foot">
            <button type="button" className="popup-btn" disabled={busy} onClick={cancel}>
              取消
            </button>
            <button
              type="button"
              className="popup-btn popup-btn--primary"
              disabled={busy || pct !== null}
              onClick={commit}
            >
              完成
            </button>
          </div>
        </div>
      </div>
    </span>
  )
}
